import React, { useState, useRef } from 'react';
import { importApi } from '../../../services/importApi';
import { BookService } from '../../../services/bookService';

interface BookImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
  importType: 'json' | 'dlow';
  bookId: string;
}

type ImportMode = 'replace' | 'append';

export function BookImportModal({ 
  isOpen, 
  onClose, 
  onImportSuccess, 
  importType,
  bookId 
}: BookImportModalProps) {
  const [jsonData, setJsonData] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<ImportMode>('append');
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Vui lòng chọn file JSON hợp lệ');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Kích thước file phải nhỏ hơn 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        setJsonData(JSON.stringify(parsed, null, 2));
        setSelectedFile(file);
        setError(null);
      } catch (err) {
        setError('File JSON không hợp lệ');
      }
    };
    reader.readAsText(file);
  };

  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonData(event.target.value);
    setError(null);
    setSelectedFile(null);
  };

  const handleImport = async () => {
    if (!jsonData.trim()) {
      setError('Vui lòng nhập dữ liệu JSON hoặc chọn file');
      return;
    }

    setIsImporting(true);
    setError(null);
    setProgress('Đang xử lý...');

    try {
      let result: any;

      if (importType === 'json') {
        // Standard JSON format
        let parsedData: any;
        try {
          parsedData = JSON.parse(jsonData);
        } catch {
          setError('Dữ liệu JSON không hợp lệ. Vui lòng kiểm tra cú pháp.');
          setProgress('');
          return;
        }

        setProgress('Đang import nội dung...');
        result = await importApi.importContentJson(bookId, parsedData, importMode);
        
      } else if (importType === 'dlow') {
        // Dlow JSON format
        const blob = new Blob([jsonData], { type: 'application/json' });
        const file = selectedFile || new File([blob], 'import.json', { type: 'application/json' });
        
        setProgress('Đang xử lý Dlow JSON...');
        result = await importApi.importContentDlowJson(bookId, file, importMode);
      }

      if (result && result.success) {
        const stats = result.data || {};
        setProgress(`Hoàn thành! Đã import ${stats.imported_chapters || 0} chương.`);
        
        // Success - close modal and refresh
        setTimeout(() => {
          handleClose();
          onImportSuccess();
        }, 1500);
      } else {
        throw new Error(result?.message || 'Import failed');
      }

    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.response?.data?.message || err.message || 'Có lỗi xảy ra khi import nội dung');
      setProgress('');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (isImporting) return; // Prevent closing while importing
    
    setJsonData('');
    setSelectedFile(null);
    setImportMode('append');
    setError(null);
    setProgress('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Import nội dung {importType === 'json' ? 'từ JSON' : 'từ Dlow JSON'}
            </h3>
            <button
              onClick={handleClose}
              disabled={isImporting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Import Mode Selection */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Chế độ import:</h4>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="append"
                  checked={importMode === 'append'}
                  onChange={(e) => setImportMode(e.target.value as ImportMode)}
                  disabled={isImporting}
                  className="mr-3 h-4 w-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Thêm vào cuối (Append)</div>
                  <div className="text-sm text-gray-600">Giữ nguyên nội dung hiện tại và thêm nội dung mới vào cuối</div>
                </div>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="replace"
                  checked={importMode === 'replace'}
                  onChange={(e) => setImportMode(e.target.value as ImportMode)}
                  disabled={isImporting}
                  className="mr-3 h-4 w-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Thay thế toàn bộ (Replace)</div>
                  <div className="text-sm text-red-600">⚠️ Xóa tất cả nội dung hiện tại và thay thế bằng nội dung mới</div>
                </div>
              </label>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File JSON
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              disabled={isImporting}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
            />
            <p className="mt-2 text-xs text-gray-500">
              Kích thước tối đa: 10MB • Định dạng: JSON
            </p>
          </div>

          {/* Divider */}
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">hoặc</span>
            </div>
          </div>

          {/* JSON Data Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dữ liệu JSON
            </label>
            <textarea
              value={jsonData}
              onChange={handleJsonChange}
              placeholder="Paste dữ liệu JSON vào đây..."
              disabled={isImporting}
              className="w-full h-48 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            />
          </div>

          {/* Progress Display */}
          {progress && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center text-blue-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm">{progress}</span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Lỗi</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gray-800">Lưu ý:</h3>
                <ul className="text-sm text-gray-700 mt-1 space-y-1">
                  <li>• Nội dung sẽ được import vào sách hiện tại</li>
                  <li>• Chế độ "Thêm vào cuối" sẽ giữ nguyên nội dung hiện có</li>
                  <li>• Chế độ "Thay thế" sẽ xóa toàn bộ nội dung cũ (không thể hoàn tác)</li>
                  <li>• Quá trình import có thể mất vài phút tùy vào số lượng nội dung</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={isImporting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Hủy
            </button>
            <button
              onClick={handleImport}
              disabled={!jsonData.trim() || isImporting}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                jsonData.trim() && !isImporting
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isImporting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Đang import...
                </div>
              ) : (
                'Import Nội Dung'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

